import moment from 'moment';
import { stringify } from 'csv-stringify/browser/esm/sync';
import { DateFormats } from 'constants/common';
import { formatCurrency } from 'utils';
import { capitalizeFirstLetter } from 'formatters';
import { differenceInDays } from 'date-fns';

import {
  FullReportResponse,
  ReportListingFilterLabels,
  ReportsListingFilters,
  ReportsListingViewModel,
} from '../../types';

const getDealStatusLabel = (status: ReportsListingViewModel['dealStatus']) => {
  switch (status) {
    case 'HEALTHY':
      return 'Healthy';
    case 'HOT':
      return 'Hot';
    case 'STALLED':
      return 'Stalled';
    case 'AT_RISK':
      return 'At Risk';
    default:
      return '';
  }
};

const getCloseDateLabel = (row: ReportsListingViewModel) => {
  if (row.closeDate) {
    return moment(row.closeDate).format(DateFormats.DayShortMonthYear);
  }

  if (row.estimatedCloseDate) {
    return moment(row.estimatedCloseDate).format(DateFormats.DayShortMonthYear);
  }

  return '';
};

const getStageLabel = (row: ReportsListingViewModel) => {
  const stage = row.stage || '';
  const department = row.department ? capitalizeFirstLetter(row.department) : '';
  const details = row.closeStatus ? (row.closeStatus === 'SUCCESS' ? 'Won' : 'Lost') : `${row.daysInStage} Days`;

  if (department) {
    return `${stage} (${department}) | ${details}`;
  }

  return `${stage} | ${details}`;
};

const getProbabilityLabel = (row: ReportsListingViewModel) => {
  if (row.probability === null || row.probability === undefined || row.probability === '') return 'N/A';
  return `${row.probability}%`;
};

const getCreatedLabel = (row: ReportsListingViewModel) => {
  if (!row.createdDateTime) return '';

  const createdAt = new Date(row.createdDateTime);
  const formatted = moment(row.createdDateTime).format(DateFormats.DateTimeMedium24Hour);
  const days = differenceInDays(new Date(), createdAt);

  return `${formatted} (${days} days)`;
};

export type GroupByValue = 'location_id' | 'sales_representative_id';

type DateInput = moment.Moment | Date | string | null | undefined;

const getGroupByColumnHeader = (groupBy: GroupByValue) => {
  if (groupBy === 'location_id') return 'Grouped Location';
  return 'Grouped Salesperson';
};

const getGroupByFileNamePart = (groupBy: GroupByValue) => {
  if (groupBy === 'location_id') return 'location';
  return 'salesperson';
};

const getFileDatePart = (value: DateInput) => {
  if (!value) return 'na';

  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : 'na';
};

const normalizeFileToken = (value: string) => {
  return value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-_]/g, '')
    .slice(0, 40);
};

const getOptionalFilterFilePart = (key: string, value?: string) => {
  const normalized = value ? normalizeFileToken(value) : '';
  return normalized ? `${key}-${normalized}` : '';
};

const getFiltersFilePart = (filters: ReportsListingFilters, labels?: ReportListingFilterLabels) => {
  const parts = [
    getOptionalFilterFilePart('search', filters.search),
    getOptionalFilterFilePart('salesperson', labels?.salesperson ?? filters.salesPersonId),
    getOptionalFilterFilePart('sales-manager', labels?.salesManager ?? filters.salesManagerId),
    getOptionalFilterFilePart('finance-manager', labels?.financeManager ?? filters.financeManagerId),
    getOptionalFilterFilePart('location', labels?.location ?? filters.locationId),
    getOptionalFilterFilePart('department', labels?.department ?? filters.departmentId),
    getOptionalFilterFilePart('stage', filters.stageName),
  ].filter(Boolean);

  return parts.length ? `-${parts.join('-')}` : '';
};

export const exportOpportunityReportToCsv = (
  list: FullReportResponse['list'],
  groupBy: GroupByValue,
  filters: ReportsListingFilters,
  labels?: ReportListingFilterLabels
) => {
  const headers = [
    getGroupByColumnHeader(groupBy),
    'Status',
    'Customer',
    'Close Date',
    'Location',
    'Invoice Amount',
    'Gross Profit',
    'Probability %',
    'Weighted Amount',
    'Stage',
    'Created',
  ];

  const rows = list.flatMap(group => {
    const groupValue = group.meta?.label || group.name || '';

    return (group.data ?? []).map(row => [
      groupValue,
      getDealStatusLabel(row.dealStatus),
      row.customer || '',
      getCloseDateLabel(row),
      row.location || 'N/A',
      row.invoiceAmount ? formatCurrency(row.invoiceAmount) : 'N/A',
      row.grossProfit !== null ? formatCurrency(row.grossProfit, true) : 'N/A',
      getProbabilityLabel(row),
      row.weightedAmount !== null ? formatCurrency(row.weightedAmount, true) : 'N/A',
      getStageLabel(row),
      getCreatedLabel(row),
    ]);
  });

  const csvContent = stringify([headers, ...rows], {
    bom: true,
  });

  const date = new Date().toISOString().slice(0, 10);
  const groupByFileNamePart = getGroupByFileNamePart(groupBy);
  const fromPart = getFileDatePart(filters.from);
  const toPart = getFileDatePart(filters.to);
  const filtersPart = getFiltersFilePart(filters, labels);
  const fileName = `opportunity-report-grouped-by-${groupByFileNamePart}-${fromPart}_to_${toPart}${filtersPart}-${date}.csv`;
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
